import React from 'react';
import { DialogActions, DialogContent, Grid2, Alert, Paper } from '@mui/material';
import { Formik, Form } from 'formik';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import FormikInput from '../../components/atom/FormikInput';
import { useAuth } from '../../hooks/useAuth';
import formatErrorToString from '../../helpers/formatErrorToString';
import getFirstAccessibleRoute from '../../helpers/getFirstAccessibleRoute';
import CommonButton from '../../components/atom/CommonButton';
import ComponentTitle from '../../components/atom/ComponentTitle';
import { useTranslation } from 'react-i18next';
import { useLogin, getGetMeQueryKey } from '@openapi/auth/auth';
import { useModals, MODAL_TYPE } from '../../hooks/useModal';
import TikiLoginPage from './TikiLoginPage';
import { useQueryClient } from '@tanstack/react-query';

function LoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, setUser, setAccessToken } = useAuth();
    const loginMutation = useLogin();
    const { showModal } = useModals();
    const queryClient = useQueryClient();

    function handleSuccess(data) {
        queryClient.removeQueries({ queryKey: getGetMeQueryKey() });
        setAccessToken(data.token);
        setUser(data.user);
    }

    if (searchParams.get('theme') === 'tiki') return <TikiLoginPage />;
    if (user) return <Navigate to={getFirstAccessibleRoute(user)} replace />;

    return (
        <Grid2
            container
            justifyContent="center"
            sx={{ minHeight: '100vh', paddingTop: '10vh', px: { xs: 2, sm: 0 } }}
        >
            <Grid2 size={{ xs: 12, sm: 8, md: 6 }}>
                <Paper sx={{ p: 1 }}>
                    <Formik
                        initialValues={{ email: '', password: '' }}
                        onSubmit={async ({ email, password }, { setSubmitting, setErrors }) => {
                            setErrors({});
                            loginMutation.reset();
                            try {
                                const data = await loginMutation.mutateAsync({
                                    data: { email, password },
                                });
                                if (!data?.user) {
                                    setErrors({ api: data?.message || t('common.error') });
                                    return;
                                }
                                handleSuccess(data);
                            } catch (error) {
                                if (formatErrorToString(error) === 'mfa_required') {
                                    showModal(MODAL_TYPE.LOGIN_TWO_FACTOR, {
                                        handleValidation: async (code) => {
                                            const data = await loginMutation.mutateAsync({
                                                data: { email, password, mfa_token: code },
                                            });
                                            if (!data?.user) {
                                                setErrors({
                                                    api: data?.message || t('common.error'),
                                                });
                                                return;
                                            }
                                            handleSuccess(data);
                                        },
                                    });
                                    return;
                                }
                                setErrors({
                                    api: formatErrorToString(error),
                                });
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                    >
                        {({ errors }) => (
                            <Form>
                                <DialogContent>
                                    <Grid2
                                        container
                                        size={{ xs: 12 }}
                                        justifyContent="center"
                                        spacing={2}
                                    >
                                        <Grid2 size={{ xs: 12 }}>
                                            <ComponentTitle variant="h4" title={t('login.title')} />
                                        </Grid2>
                                        {errors?.api && (
                                            <Grid2 container size={{ xs: 12 }} alignItems="center">
                                                <Grid2 size={{ xs: 12 }}>
                                                    <Alert severity="error">{errors?.api}</Alert>
                                                </Grid2>
                                            </Grid2>
                                        )}
                                        <FormikInput
                                            sm={12}
                                            name="email"
                                            label={t('login.email')}
                                            required
                                            type="email"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <FormikInput
                                            sm={12}
                                            name="password"
                                            label={t('login.password')}
                                            required
                                            type="password"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid2>
                                </DialogContent>
                                <DialogActions
                                    sx={{
                                        '@media (max-width:375px)': {
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            gap: 1,
                                            '& > :not(:first-of-type)': { ml: 0 },
                                        },
                                    }}
                                >
                                    <CommonButton
                                        onClick={() => {
                                            navigate('/forgotpassword');
                                        }}
                                        label={t('login.forgotPassword')}
                                        buttonType="text"
                                    />
                                    <CommonButton
                                        type="submit"
                                        label={t('login.login')}
                                        buttonType="primary"
                                        disabled={loginMutation.isPending}
                                    />
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </Paper>
            </Grid2>
        </Grid2>
    );
}

export default LoginPage;
